import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Layout, Typography, Button, Drawer, Form, Modal, message, Select, Input, Row, Col, Card, Spin, Space, Tag, Progress } from 'antd';
import { PlusOutlined, SearchOutlined, LinkOutlined, FilterOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import ProjectKanban from '../../components/Projects/ProjectKanban';
import ProjectDetails from '../../components/Projects/ProjectDetails';
import ThreadDrawer from '../../components/Projects/ThreadDrawer';
import { Project, Retailer, summarizeRetailerEmail, summarizeRetailerThread, updateRetailerEmailImportance } from '../../services/retailerApi';
import { Supplier } from '../../services/supplierApi';
import { Storage, getStorages } from '../../services/storageApi';
import { User, getAllUsers } from '../../services/userService';
import { getProjects, createProjectGlobal, updateProjectGlobal, deleteProjectGlobal } from '../../services/projectApi';
import { getRetailers } from '../../services/retailerApi';
import { ProjectActionType, ProjectContextPayload, runProjectAction } from '../../services/aiProjectService';
import api from '../../services/api';
import { useAppSelector, useAppDispatch } from '../../store';
import { removeHotMemoryItem, toggleHotMemoryPin, clearHotMemory } from '../../store/hotMemorySlice';
import { HotMemoryItemType } from '../../store/hotMemorySlice';

const { Content } = Layout;
const { Title, Text } = Typography;

const ProjectsPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const [projects, setProjects] = useState<Project[]>([]);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [form] = Form.useForm();
  const [retailers, setRetailers] = useState<Retailer[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [storages, setStorages] = useState<Storage[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [dragOverColumn, setDragOverColumn] = useState<string | null>(null);
  const [isOrphanModalOpen, setIsOrphanModalOpen] = useState(false);
  const [orphanEmails, setOrphanEmails] = useState<any[]>([]);
  const [orphanLoading, setOrphanLoading] = useState(false);
  const [attachLoadingByConversation, setAttachLoadingByConversation] = useState<Record<string, boolean>>({});
  const [refreshingProjectIds, setRefreshingProjectIds] = useState<Record<string, boolean>>({});
  const [orphanSearch, setOrphanSearch] = useState('');
  const [orphanMailboxFilter, setOrphanMailboxFilter] = useState<string | undefined>(undefined);
  const [orphanReadFilter, setOrphanReadFilter] = useState<'all' | 'unread' | 'read'>('unread');
  const [projectEmails, setProjectEmails] = useState<any[]>([]);
  const [blacklistModalOpen, setBlacklistModalOpen] = useState(false);
  const [blacklistEntries, setBlacklistEntries] = useState<any[]>([]);
  const [blacklistLoading, setBlacklistLoading] = useState(false);
  const [blacklistEmail, setBlacklistEmail] = useState('');
  const [blacklistReason, setBlacklistReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [projectEmailsLoading, setProjectEmailsLoading] = useState(false);
  const [searchFilter, setSearchFilter] = useState("");
  const [priorityFilter, setPriorityFilter] = useState<string | null>(null);
  const [retailerFilter, setRetailerFilter] = useState<string | null>(null);
  const [supplierFilter, setSupplierFilter] = useState<string | null>(null);
  const [showRetailerProjects, setShowRetailerProjects] = useState(false);
  const [showSupplyProjects, setShowSupplyProjects] = useState(false);
  const [activeConversationId, setActiveConversationId] = useState<string | null>(null);
  const [isConversationDrawerOpen, setIsConversationDrawerOpen] = useState(false);
  const [aiOutputsByAction, setAiOutputsByAction] = useState<Record<string, any>>({});
  const [aiWarningsByAction, setAiWarningsByAction] = useState<Record<string, string[]>>({});
  const [aiLoadingByAction, setAiLoadingByAction] = useState<Record<string, boolean>>({});
  const [summaryLoadingById, setSummaryLoadingById] = useState<Record<string, boolean>>({});
  const [messageDraft, setMessageDraft] = useState('');
  const autoSnapshotRef = useRef<{ projectId?: string; messageId?: string } | null>(null);

  const normalizeId = (value: any): string =>
    String(value?._id || value?.$oid || value || "");

  const { currentUser } = useAppSelector((state) => state.user);
  const isAdmin = Boolean(currentUser?.accesses?.includes('admin'));
  const hotMemoryItems = useAppSelector((state) =>
    currentUser ? state.hotMemory.itemsByUser[currentUser.id] || [] : []
  );
  const lastHotMemoryProjectRef = useRef<string | null>(null);

  const filteredProjects = useMemo(() => {
    return projects.filter(p => {
      if (p.status === 'archived') return false;
      const matchesSearch = p.title.toLowerCase().includes(searchFilter.toLowerCase()) || 
                           p.description?.toLowerCase().includes(searchFilter.toLowerCase());
      const matchesPriority = !priorityFilter || p.priority === priorityFilter;
      const matchesRetailer = !retailerFilter || p.retailerId === retailerFilter;
      const matchesSupplier = !supplierFilter || p.supplierId === supplierFilter;
      const matchesRetailerScope = showRetailerProjects || !p.retailerId;
      const matchesSupplyScope = showSupplyProjects || p.metadata?.supplyProject !== 'true';
      return (
        matchesSearch &&
        matchesPriority &&
        matchesRetailer &&
        matchesSupplier &&
        matchesRetailerScope &&
        matchesSupplyScope
      );
    });
  }, [
    projects,
    searchFilter,
    priorityFilter,
    retailerFilter,
    supplierFilter,
    showRetailerProjects,
    showSupplyProjects,
  ]);

  const orphanMailboxes = useMemo(() => {
    return Array.from(
      new Set(orphanEmails.map(email => email.mailboxEmail).filter(Boolean))
    ).sort((a, b) => a.localeCompare(b));
  }, [orphanEmails]);

  const filteredOrphanEmails = useMemo(() => {
    const search = orphanSearch.trim().toLowerCase();
    return orphanEmails.filter(email => {
      if (orphanMailboxFilter && email.mailboxEmail !== orphanMailboxFilter) return false;
      if (orphanReadFilter === 'unread' && email.isRead) return false;
      if (orphanReadFilter === 'read' && !email.isRead) return false;
      if (!search) return true;
      const subject = (email.subject || '').toLowerCase();
      const preview = (email.preview || '').toLowerCase();
      const from = (email.from || '').toLowerCase();
      return subject.includes(search) || preview.includes(search) || from.includes(search);
    });
  }, [orphanEmails, orphanMailboxFilter, orphanReadFilter, orphanSearch]);

  const kanbanData = useMemo(() => {
    return {
      todo: filteredProjects.filter(p => p.status === 'open' && p.pushPullStatus === 'push'),
      in_progress: filteredProjects.filter(p => p.status === 'open' && p.pushPullStatus === 'pull'),
      on_hold: filteredProjects.filter(p => p.status === 'on_hold'),
      closed: filteredProjects.filter(p => p.status === 'completed' || p.status === 'cancelled')
    };
  }, [filteredProjects]);

  const currentUserEmail = useMemo(() => {
    if (!currentUser?.id) return undefined;
    const match = users.find((user) => user._id === currentUser.id);
    return match?.email;
  }, [currentUser?.id, users]);

  const collaboratorEmails = useMemo(() => {
    if (!selectedProject?.collaborators?.length) return [] as string[];
    const ids = new Set(selectedProject.collaborators.map((id: any) => String(id)));
    return users
      .filter((user) => user._id && ids.has(String(user._id)) && user.email)
      .map((user) => user.email as string);
  }, [selectedProject?.collaborators, users]);

  const parseDraftReply = (output?: Record<string, any>) => {
    if (!output) return "";
    return (
      output?.draftReply?.body ||
      output?.draftReply ||
      output?.reply ||
      output?.email?.body ||
      output?.body ||
      ""
    );
  };

  const summarizeMessage = async (retailerId: string, mail: any) => {
    if (!mail?.id) return;
    const key = mail.id;
    setSummaryLoadingById((prev) => ({ ...prev, [key]: true }));
    try {
      const result = await summarizeRetailerEmail(retailerId, mail.id, {
        subject: mail.subject,
        preview: mail.preview,
      });
      setProjectEmails((prev) =>
        prev.map((item) =>
          item.id === mail.id ? { ...item, summary: result.summary } : item
        )
      );
    } catch (error) {
      message.error("Erreur lors du résumé IA");
    } finally {
      setSummaryLoadingById((prev) => ({ ...prev, [key]: false }));
    }
  };

  const fetchBlacklistEntries = async () => {
    setBlacklistLoading(true);
    try {
      const entries = await api.get('/emails/blacklist');
      setBlacklistEntries(Array.isArray(entries) ? entries : []);
    } catch (error) {
      message.error("Erreur lors de la récupération de la blacklist");
    } finally {
      setBlacklistLoading(false);
    }
  };

  const triggerSnapshotIfNeeded = async (project: Project, emails: any[]) => {
    if (!emails.length) return;
    const sorted = [...emails].sort(
      (a, b) => new Date(b.receivedDateTime || 0).getTime() - new Date(a.receivedDateTime || 0).getTime()
    );
    const latest = sorted[0];
    if (!latest?.id) return;
    const lastSnapshot = project.metadata?.lastSnapshotMessageId;
    if (lastSnapshot === latest.id) return;
    if (
      autoSnapshotRef.current?.projectId === project._id &&
      autoSnapshotRef.current?.messageId === latest.id
    ) {
      return;
    }
    autoSnapshotRef.current = { projectId: project._id, messageId: latest.id };
    await runAiProjectAction({
      action: 'PROJECT_SNAPSHOT',
      project,
      target: { threadId: latest.conversationId, messageId: latest.id },
      emailsOverride: emails,
    });
  };

  const forceProjectSnapshot = async (project: Project, emails: any[]) => {
    const sorted = [...(emails || [])].sort(
      (a, b) => new Date(b.receivedDateTime || 0).getTime() - new Date(a.receivedDateTime || 0).getTime()
    );
    const latest = sorted[0];
    await runAiProjectAction({
      action: 'PROJECT_SNAPSHOT',
      project,
      target: latest?.id ? { threadId: latest.conversationId, messageId: latest.id } : undefined,
      emailsOverride: emails || [],
    });
  };

  const handleAddBlacklistEntry = async () => {
    const email = blacklistEmail.trim().toLowerCase();
    if (!email) {
      message.warning("Adresse email requise");
      return;
    }
    try {
      await api.post('/emails/blacklist', {
        email,
        reason: blacklistReason.trim(),
        active: true,
      });
      setBlacklistEmail('');
      setBlacklistReason('');
      fetchBlacklistEntries();
    } catch (error) {
      message.error("Erreur lors de l'ajout à la blacklist");
    }
  };

  const handleDeleteBlacklistEntry = async (id: string) => {
    try {
      await api.delete(`/emails/blacklist/${id}`);
      setBlacklistEntries((prev) => prev.filter((entry) => entry._id !== id));
    } catch (error) {
      message.error("Erreur lors de la suppression");
    }
  };

  console.log('Kanban stats:', Object.keys(kanbanData).map(k => `${k}: ${kanbanData[k as keyof typeof kanbanData].length}`));

  useEffect(() => {
    fetchProjects();
    fetchRetailers();
    fetchSuppliers();
    fetchStorages();
    fetchUsers();
  }, []);

  useEffect(() => {
    if (!currentUser) return;
    const nextId = selectedProject?._id || null;
    if (lastHotMemoryProjectRef.current && nextId && lastHotMemoryProjectRef.current !== nextId) {
      dispatch(clearHotMemory({ userId: currentUser.id }));
    }
    lastHotMemoryProjectRef.current = nextId;
  }, [selectedProject?._id, currentUser, dispatch]);

  const fetchOrphanEmails = async () => {
    setOrphanLoading(true);
    try {
      const emails = await api.get('/emails/orphan');
      setOrphanEmails(Array.isArray(emails) ? emails : []);
    } catch (error) {
      message.error("Erreur lors de la récupération des emails orphelins");
    } finally {
      setOrphanLoading(false);
    }
  };

  const fetchProjectEmails = async (project: Project | null) => {
    if (!project) {
      setProjectEmails([]);
      return [] as any[];
    }
    try {
      setProjectEmailsLoading(true);
      const emails = await api.get(`/projects/${project._id}/emails/attached`);
      const safeEmails = Array.isArray(emails) ? emails : [];
      setProjectEmails(safeEmails);
      await refreshProjectEmailStats(project._id!, safeEmails);
      await triggerSnapshotIfNeeded(project, safeEmails);
      return safeEmails;
    } catch (error) {
      setProjectEmails([]);
      message.error("Erreur lors de la récupération des emails projet");
      return [] as any[];
    } finally {
      setProjectEmailsLoading(false);
    }
  };

  const computeProjectEmailStats = (emails: any[]) => {
    if (!emails.length) return {};
    const sorted = [...emails].sort(
      (a, b) => new Date(b.receivedDateTime || 0).getTime() - new Date(a.receivedDateTime || 0).getTime()
    );
    const latest = sorted[0];
    const latestDate = latest?.receivedDateTime
      ? new Date(latest.receivedDateTime).toISOString()
      : undefined;
    return {
      lastEmailAt: latestDate,
      lastEmailFrom: latest?.from || undefined,
      lastEmailSubject: latest?.subject || undefined,
      lastEmailPreview: latest?.preview || undefined,
      unreadEmailCount: sorted.filter((email) => !email.isRead).length,
    };
  };

  const refreshProjectEmailStats = async (projectId: string, emails?: any[]) => {
    const sourceEmails = emails || projectEmails;
    if (!sourceEmails.length) return;
    const stats = computeProjectEmailStats(sourceEmails);
    if (!Object.keys(stats).length) return;
    try {
      const data = await updateProjectGlobal(projectId, stats);
      setProjects(prev => prev.map(p => p._id === projectId ? { ...p, ...data } : p));
      setSelectedProject(prev => prev && prev._id === projectId ? { ...prev, ...data } : prev);
    } catch (error) {
      message.error("Erreur lors de la mise à jour du projet");
    }
  };

  const handleAttachOrphan = async (email: any, projectId: string) => {
    const key = email.conversationId || email.id || '';
    if (!key) return;
    try {
      setAttachLoadingByConversation((prev) => ({ ...prev, [key]: true }));
      setRefreshingProjectIds((prev) => ({ ...prev, [projectId]: true }));
      const project = projects.find(p => p._id === projectId);
      if (!project) return;

      const attachedConversationIds = Array.from(
        new Set([...(project.attachedConversationIds || []), email.conversationId])
      );
      await updateProjectGlobal(projectId, { attachedConversationIds });

      setProjects(prev => prev.map(p => p._id === projectId ? { ...p, attachedConversationIds } : p));
      setOrphanEmails(prev => prev.filter(e => e.conversationId !== email.conversationId));
      message.success("Conversation rattachée au projet");

      const attachedEmails = await api.get(`/projects/${projectId}/emails/attached`);
      const safeEmails = Array.isArray(attachedEmails) ? attachedEmails : [];
      if (selectedProject?._id === projectId) {
        setProjectEmails(safeEmails);
      }
      await refreshProjectEmailStats(projectId, safeEmails);
      await forceProjectSnapshot(project, safeEmails);
    } catch (error) {
      message.error("Erreur lors du rattachement");
    } finally {
      setAttachLoadingByConversation((prev) => ({ ...prev, [key]: false }));
      setRefreshingProjectIds((prev) => ({ ...prev, [projectId]: false }));
    }
  };

  const fetchProjects = async () => {
    setLoading(true);
    try {
      const data = await getProjects();
      setProjects(data);
    } catch (error) {
      message.error("Erreur lors de la récupération des projets");
    } finally {
      setLoading(false);
    }
  };

  const fetchRetailers = async () => {
    try {
      const data = await getRetailers();
      setRetailers(data);
    } catch (error) {
      console.error("Error fetching retailers", error);
    }
  };

  const fetchSuppliers = async () => {
    try {
      const res = await api.get('/supply/suppliers');
      setSuppliers(res);
    } catch (error) {
      console.error("Error fetching suppliers", error);
    }
  };

  const fetchStorages = async () => {
    try {
      const data = await getStorages();
      setStorages(data);
    } catch (error) {
      console.error("Error fetching storages", error);
    }
  };

  const fetchUsers = async () => {
    try {
      const data = await getAllUsers();
      setUsers(data);
    } catch (error) {
      console.error("Error fetching users", error);
    }
  };

  const handleCardClick = (projectId: string) => {
    const project = projects.find(p => p._id === projectId);
    if (project) {
      setSelectedProject(project);
      setIsDrawerOpen(true);
      fetchProjectEmails(project);
    }
  };

  const handleDeleteProject = async (projectId: string) => {
    Modal.confirm({
      title: 'Supprimer le projet',
      content: 'Êtes-vous sûr de vouloir supprimer ce projet ? Cette action est irréversible.',
      okText: 'Supprimer',
      okType: 'danger',
      cancelText: 'Annuler',
      onOk: async () => {
        try {
          await deleteProjectGlobal(projectId);
          setProjects(prev => prev.filter(p => p._id !== projectId));
          message.success("Projet supprimé");
        } catch (error) {
          message.error("Erreur lors de la suppression");
        }
      }
    });
  };

  const handleDrop = async (projectId: string, target: string) => {
    setDragOverColumn(null);
    const project = projects.find(p => p._id === projectId);
    if (!project) return;

    let updates: Partial<Project> = {};
    if (target === 'todo') {
      updates = { status: 'open', pushPullStatus: 'push' };
    } else if (target === 'in_progress') {
      updates = { status: 'open', pushPullStatus: 'pull' };
    } else {
      updates = { status: 'completed' };
    }

    try {
      const data = await updateProjectGlobal(projectId, updates);
      setProjects(prev => prev.map(p => p._id === projectId ? { ...p, ...data } : p));
    } catch (error) {
      message.error("Erreur lors de la mise à jour du statut");
    }
  };

  const handleSaveProject = async (values: any) => {
    try {
      if (selectedProject?._id) {
        const data = await updateProjectGlobal(selectedProject._id, values);
        setProjects(prev => prev.map(p => p._id === selectedProject._id ? { ...p, ...data } : p));
        setSelectedProject({ ...selectedProject, ...data });
        message.success("Projet mis à jour");
      } else {
        const data = await createProjectGlobal(values);
        setProjects(prev => [data, ...prev]);
        message.success("Projet créé");
      }
      setIsModalOpen(false);
      form.resetFields();
    } catch (error) {
      message.error("Erreur lors de l'enregistrement");
    }
  };

  const getContactDisplayName = (_retailerId: string, email?: string) => {
    if (!email) return '—';
    const retailer = retailers.find(r => (r.id || r._id) === _retailerId);
    if (!retailer) return email.split('@')[0];
    const contact = (retailer.contacts || []).find(c => c.email?.toLowerCase() === email.toLowerCase());
    return contact?.name || email.split('@')[0];
  };

  // const runAiAction = async () => {};
  const getSnapshotPayload = (project: Project) => {
    const snapshotKey = `PROJECT_SNAPSHOT:${project._id || project.title}`;
    const live = aiOutputsByAction[snapshotKey];
    if (live) return live;
    try {
      return project.metadata?.snapshotPayload ? JSON.parse(project.metadata.snapshotPayload) : null;
    } catch {
      return null;
    }
  };

  const extractProjectTasks = (project: Project): string[] => {
    const board = project.taskBoard || {};
    const tasks = [
      ...(board.todo || []),
      ...(board.in_progress || []),
    ] as Array<{ title?: string }>;
    return tasks
      .map((task) => task.title)
      .filter((title): title is string => typeof title === "string" && title.trim().length > 0);
  };

  const buildProjectContext = (project: Project, emails: any[]): ProjectContextPayload => {
    const projectKey = project._id || project.title;
    const projectType = project.retailerId
      ? 'retailer'
      : project.supplierId
      ? 'supplier'
      : project.storageId
      ? 'storage'
      : 'standalone';
    const latestEmail = [...(emails || [])].sort(
      (a, b) => new Date(b.receivedDateTime || 0).getTime() - new Date(a.receivedDateTime || 0).getTime()
    )[0];
    const participants = Array.from(
      new Set(emails.map((email) => email.from).filter(Boolean))
    ) as string[];
    return {
      projectKey,
      projectType,
      title: project.title,
      description: project.description,
      lastEmailSubject: latestEmail?.subject || project.lastEmailSubject,
      lastEmailPreview: latestEmail?.preview || project.lastEmailPreview,
      lastEmailAt: latestEmail?.receivedDateTime
        ? new Date(latestEmail.receivedDateTime).toISOString()
        : project.lastEmailAt
        ? new Date(project.lastEmailAt).toISOString()
        : undefined,
      lastEmailFrom: latestEmail?.from || project.lastEmailFrom,
      participants,
      currentTasks: extractProjectTasks(project),
      attachments: (project.attachments || []).map((att) => ({
        name: att.name,
        contentType: att.contentType,
      })),
      relatedEntities: {
        retailerId: project.retailerId,
        supplierId: project.supplierId,
      },
      language: 'FR',
    };
  };

  const extractSnapshotTasks = (payload: any): string[] => {
    const tasks = Array.isArray(payload?.tasks) ? payload.tasks : [];
    return tasks
      .map((task: any) => task?.title || task?.task || task?.label)
      .filter((task: any) => typeof task === 'string' && task.trim().length > 0);
  };

  const buildUserSignature = () => {
    if (!currentUser) return undefined;
    const fullName = [currentUser.firstName, currentUser.lastName].filter(Boolean).join(' ');
    const lines = [
      'Bien à vous,',
      fullName,
      currentUser.function,
      currentUser.companyName || 'Honnoré Services',
    ].filter((line) => typeof line === 'string' && line.trim().length > 0);
    return lines.join('\n');
  };

  const runAiProjectAction = async ({
    action,
    project,
    target,
    emailsOverride,
    styleSamples,
  }: {
    action: ProjectActionType;
    project: Project;
    target?: { threadId?: string; messageId?: string };
    emailsOverride?: any[];
    styleSamples?: string[];
  }) => {
    const actionKey = `${action}:${project._id || project.title}`;
    setAiLoadingByAction((prev) => ({ ...prev, [actionKey]: true }));
    try {
      const result = await runProjectAction({
        action,
        projectContext: buildProjectContext(project, emailsOverride || projectEmails),
        projectRef: {
          projectId: project._id || '',
        },
        target,
        hotMemory: [],
        styleProfile: {
          tone: 'professionnel',
          length: 'medium',
          samples: styleSamples,
          closing: buildUserSignature(),
        },
        recipientProfile: { relation: 'standard', formality: 'professionnel' },
      });

      setAiOutputsByAction((prev) => ({ ...prev, [actionKey]: result.structuredOutput }));
      setAiWarningsByAction((prev) => ({ ...prev, [actionKey]: result.warnings || [] }));

      if (action === 'PROJECT_SNAPSHOT') {
        const metadata = {
          ...(project.metadata || {}),
          lastSnapshotMessageId: target?.messageId || '',
          lastSnapshotAt: new Date().toISOString(),
          snapshotPayload: JSON.stringify(result.structuredOutput || {}),
        } as Record<string, string>;
        const stageKey = project.pushPullStatus === 'pull' ? 'in_progress' : 'todo';
        const snapshotTasks = extractSnapshotTasks(result?.structuredOutput || {});
        const existingBoard = project.taskBoard || {};
        const stageTasks = existingBoard?.[stageKey] || [];
        const existingTitles = new Set(stageTasks.map((task: any) => task.title));
        const mergedStageTasks = snapshotTasks.length
          ? [
              ...stageTasks,
              ...snapshotTasks
                .filter((task) => !existingTitles.has(task))
                .map((task) => ({
                  title: task,
                  status: 'active',
                  source: 'ai',
                  createdAt: new Date().toISOString(),
                })),
            ]
          : stageTasks;
        const taskBoard = { ...existingBoard, [stageKey]: mergedStageTasks };

        const data = await updateProjectGlobal(project._id!, { metadata, taskBoard });
        setProjects(prev => prev.map(p => p._id === project._id ? { ...p, ...data } : p));
        setSelectedProject(prev => prev ? { ...prev, ...data } : null);
      }

      return result;
    } catch (error) {
      const msg = error instanceof Error ? error.message : 'Erreur inconnue';
      message.error(`Action IA impossible: ${msg}`);
      return undefined;
    } finally {
      setAiLoadingByAction((prev) => ({ ...prev, [actionKey]: false }));
    }
  };

  useEffect(() => {
    if (!selectedProject || !projectEmails.length) return;
    void triggerSnapshotIfNeeded(selectedProject, projectEmails);
  }, [selectedProject, projectEmails]);

  useEffect(() => {
    if (!isDrawerOpen || !selectedProject || !isConversationDrawerOpen) return undefined;
    return undefined;
  }, [isDrawerOpen, selectedProject, isConversationDrawerOpen]);

  const threadDrawer = (
    <ThreadDrawer
      open={isConversationDrawerOpen}
      activeConversationId={activeConversationId}
      emails={projectEmails}
      onClose={() => {
        setIsConversationDrawerOpen(false);
        setActiveConversationId(null);
      }}
      onSummarizeMessage={selectedProject?.retailerId
        ? (msg) => summarizeMessage(selectedProject.retailerId as string, msg)
        : undefined}
      summaryLoadingById={summaryLoadingById}
      onGenerateDraftReply={async ({ latestMessage, threadMessages, styleSamples }) => {
        if (!selectedProject) return undefined;
        const result = await runAiProjectAction({
          action: "DRAFT_REPLY",
          project: selectedProject,
          emailsOverride: threadMessages,
          styleSamples,
          target: {
            threadId: latestMessage?.conversationId,
            messageId: latestMessage?.id,
          },
        });
        const draft = parseDraftReply(result?.structuredOutput);
        return draft || undefined;
      }}
      isGeneratingDraft={Boolean(
        selectedProject && aiLoadingByAction[`DRAFT_REPLY:${selectedProject._id || selectedProject.title}`]
      )}
      messageDraft={messageDraft}
      onMessageDraftChange={setMessageDraft}
      onUpdateMessage={(messageId, updates) =>
        setProjectEmails((prev) => prev.map((item) => (item.id === messageId ? { ...item, ...updates } : item)))
      }
      onToggleImportant={async (messageId, nextImportant) => {
        if (!selectedProject?.retailerId) return;
        await updateRetailerEmailImportance(selectedProject.retailerId, messageId, nextImportant);
      }}
      onSummarizeThread={selectedProject?.retailerId
        ? async (threadId) => {
            const result = await summarizeRetailerThread(selectedProject.retailerId as string, threadId);
            setProjectEmails((prev) =>
              prev.map((item) =>
                item.conversationId === threadId ? { ...item, threadSummary: result.summary } : item
              )
            );
            return result.summary;
          }
        : undefined}
      currentUserEmail={currentUserEmail}
      collaboratorEmails={collaboratorEmails}
    />
  );

  return (
    <Content style={{ padding: '24px', background: '#f8fafc', minHeight: '100vh' }}>
      <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
        <div>
          <Title level={2} style={{ margin: 0, fontWeight: 800, color: '#0f172a', letterSpacing: '-0.02em' }}>
            Gestion des Projets
          </Title>
          <Typography.Text type="secondary" style={{ fontSize: 14 }}>Vue d'ensemble et orchestration des flux opérationnels</Typography.Text>
        </div>
        <Space size={12}>
          <Button 
            icon={<LinkOutlined />} 
            onClick={async () => {
              setIsOrphanModalOpen(true);
              if (!projects.length) {
                await fetchProjects();
              }
              fetchOrphanEmails();
            }}
            style={{ borderRadius: 8, height: 40, fontWeight: 600 }}
          >
            Mails Orphelins
          </Button>
          <Button 
            type="primary" 
            icon={<PlusOutlined />} 
            onClick={() => {
              setSelectedProject(null);
              form.resetFields();
              setIsModalOpen(true);
            }}
            style={{ borderRadius: 8, height: 40, fontWeight: 600, boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
          >
            Nouveau Projet
          </Button>
        </Space>
      </div>

      <div style={{
        background: '#fff',
        padding: '16px 20px',
        borderRadius: 16,
        border: '1px solid #e2e8f0',
        marginBottom: 24,
        boxShadow: '0 1px 2px 0 rgb(0 0 0 / 0.05)'
      }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ position: 'relative', maxWidth: 420 }}>
            <Input
              prefix={<SearchOutlined style={{ color: '#94a3b8' }} />}
              placeholder="Rechercher par titre ou description..."
              value={searchFilter}
              onChange={e => setSearchFilter(e.target.value)}
              style={{ borderRadius: 10, height: 42, paddingLeft: 12 }}
              allowClear
            />
          </div>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            flexWrap: 'nowrap',
            overflowX: 'auto',
            paddingBottom: 4
          }}>
            <Select
              style={{ width: 140 }}
              placeholder={<Space><FilterOutlined />Priorité</Space>}
              allowClear
              value={priorityFilter}
              onChange={setPriorityFilter}
              className="custom-select-premium"
            >
              <Select.Option value="high"><Tag color="red">Haute</Tag></Select.Option>
              <Select.Option value="medium"><Tag color="orange">Moyenne</Tag></Select.Option>
              <Select.Option value="low"><Tag color="green">Basse</Tag></Select.Option>
            </Select>

            <Select
              style={{ width: 180 }}
              placeholder="Distributeur"
              allowClear
              showSearch
              optionFilterProp="children"
              value={retailerFilter}
              onChange={setRetailerFilter}
            >
              {retailers.map(r => (
                <Select.Option key={r._id || r.id} value={r._id || r.id}>{r.name}</Select.Option>
              ))}
            </Select>

            <Select
              style={{ width: 180 }}
              placeholder="Fournisseur"
              allowClear
              showSearch
              optionFilterProp="children"
              value={supplierFilter}
              onChange={setSupplierFilter}
            >
              {suppliers.map(s => (
                <Select.Option key={s._id || s.id} value={s._id || s.id}>{s.name}</Select.Option>
              ))}
            </Select>

            <Button onClick={() => setShowRetailerProjects((prev) => !prev)}>
              {showRetailerProjects ? 'Masquer projets retailers' : 'Afficher projets retailers'}
            </Button>
            <Button onClick={() => setShowSupplyProjects((prev) => !prev)}>
              {showSupplyProjects ? 'Masquer projets supply' : 'Afficher projets supply'}
            </Button>
            {isAdmin && (
              <Button onClick={() => {
                setBlacklistModalOpen(true);
                fetchBlacklistEntries();
              }}>
                Blacklist emails
              </Button>
            )}

            <div style={{
              padding: '0 12px',
              height: 32,
              background: '#f1f5f9',
              borderRadius: 20,
              display: 'flex',
              alignItems: 'center',
              border: '1px solid #e2e8f0'
            }}>
              <Text strong style={{ fontSize: 12, color: '#475569' }}>
                {filteredProjects.length} <Text type="secondary" style={{ fontSize: 11 }}>projets</Text>
              </Text>
            </div>
          </div>
        </div>
      </div>

      <ProjectKanban
        projects={filteredProjects}
        onCardClick={handleCardClick}
        onDeleteProject={handleDeleteProject}
        onDrop={handleDrop}
        onDragOver={setDragOverColumn}
        dragOverColumn={dragOverColumn}
        activeProjectId={selectedProject?._id}
        getContactDisplayName={getContactDisplayName}
        refreshingProjectIds={refreshingProjectIds}
      />
      {loading && (
        <div style={{ textAlign: 'center', marginTop: 24 }}>
          <Spin size="large" />
        </div>
      )}

      <Drawer
        title="Détails du Projet"
        width={1000}
        onClose={() => {
          setIsDrawerOpen(false);
          setSelectedProject(null);
          setProjectEmails([]);
        }}
        open={isDrawerOpen}
        styles={{ body: { padding: 0 } }}
      >
        {selectedProject && (
          <ProjectDetails
            project={selectedProject}
            retailer={retailers.find(r => (r.id || r._id) === selectedProject.retailerId) || { name: 'Standalone' } as Retailer}
            retailers={retailers}
            suppliers={suppliers}
            storages={storages}
            users={users}
            retailerEmails={projectEmails} // emails attachés au projet
            emailsLoading={projectEmailsLoading}
            onAddTodo={async (stage, task) => {
              const currentBoard = (selectedProject.taskBoard as any) || {};
              const stageTasks = [...(currentBoard[stage] || [])];
              stageTasks.push({ title: task, status: 'active', source: 'manual', createdAt: new Date().toISOString() });
              const taskBoard = { ...currentBoard, [stage]: stageTasks };
              const data = await updateProjectGlobal(selectedProject._id!, { taskBoard });
              setProjects(prev => prev.map(p => p._id === selectedProject._id ? { ...p, taskBoard: data.taskBoard } : p));
              setSelectedProject(prev => prev ? { ...prev, taskBoard: data.taskBoard } : null);
            }}
            onToggleTodo={async (stage, idx) => {
              const currentBoard = (selectedProject.taskBoard as any) || {};
              const stageTasks = [...(currentBoard[stage] || [])];
              const task = stageTasks[idx];
              const nextStatus = task.status === 'archived' ? 'active' : 'archived';
              stageTasks[idx] = { ...task, status: nextStatus, archivedAt: nextStatus === 'archived' ? new Date().toISOString() : undefined };
              const taskBoard = { ...currentBoard, [stage]: stageTasks };
              const data = await updateProjectGlobal(selectedProject._id!, { taskBoard });
              setProjects(prev => prev.map(p => p._id === selectedProject._id ? { ...p, taskBoard: data.taskBoard } : p));
              setSelectedProject(prev => prev ? { ...prev, taskBoard: data.taskBoard } : null);
            }}
            onDeleteTodo={async (stage, idx) => {
              const currentBoard = (selectedProject.taskBoard as any) || {};
              const stageTasks = [...(currentBoard[stage] || [])];
              stageTasks.splice(idx, 1);
              const taskBoard = { ...currentBoard, [stage]: stageTasks };
              const data = await updateProjectGlobal(selectedProject._id!, { taskBoard });
              setProjects(prev => prev.map(p => p._id === selectedProject._id ? { ...p, taskBoard: data.taskBoard } : p));
              setSelectedProject(prev => prev ? { ...prev, taskBoard: data.taskBoard } : null);
            }}
            onThreadClick={(threadId) => {
              setActiveConversationId(threadId);
              setIsConversationDrawerOpen(true);
            }}
            onToggleHotMemoryPin={(id, type) => currentUser && dispatch(toggleHotMemoryPin({ userId: currentUser.id, id, type: type as HotMemoryItemType }))}
            onRemoveHotMemory={(id, type) => currentUser && dispatch(removeHotMemoryItem({ userId: currentUser.id, id, type: type as HotMemoryItemType }))}
            onClearHotMemory={() => currentUser && dispatch(clearHotMemory({ userId: currentUser.id }))}
            hotMemoryItems={hotMemoryItems}
            getContactDisplayName={(retailer, email) => getContactDisplayName(retailer.id || retailer._id || '', email)}
            aiWarningsByAction={{}}
            snapshotLoading={Boolean(aiLoadingByAction[`PROJECT_SNAPSHOT:${selectedProject._id || selectedProject.title}`])}
            getSnapshotPayload={getSnapshotPayload}
            onUpdateProject={async (updates) => {
              try {
                const data = await updateProjectGlobal(selectedProject._id!, updates);
                setProjects(prev => prev.map(p => p._id === selectedProject._id ? { ...p, ...data } : p));
                setSelectedProject(prev => prev ? { ...prev, ...data } : null);
                let emails = projectEmails;
                if (updates.attachedEmailIds || updates.attachedConversationIds) {
                  emails = await fetchProjectEmails(data);
                }
                await forceProjectSnapshot(data, emails);
              } catch (error) {
                message.error("Erreur lors de la mise à jour");
              }
            }}
          />
        )}
      </Drawer>

      {threadDrawer}

      <Modal
        title={
          <Space>
            <LinkOutlined style={{ color: '#3b82f6' }} />
            <span>Mails Orphelins - Rattachement</span>
          </Space>
        }
        open={isOrphanModalOpen}
        onCancel={() => setIsOrphanModalOpen(false)}
        footer={null}
        width={900}
        styles={{ body: { padding: '0 0 24px 0' } }}
      >
        <div style={{ maxHeight: 600, overflowY: 'auto', padding: '0 24px' }}>
          <div
            style={{
              display: 'flex',
              gap: 12,
              flexWrap: 'wrap',
              marginBottom: 16,
              padding: '12px 16px',
              background: '#f8fafc',
              borderRadius: 12,
              border: '1px solid #e2e8f0',
            }}
          >
            <Input
              value={orphanSearch}
              onChange={(e) => setOrphanSearch(e.target.value)}
              placeholder="Recherche (objet, expéditeur, contenu)"
              prefix={<SearchOutlined style={{ color: '#94a3b8' }} />}
              allowClear
              style={{ width: 260 }}
            />
            <Select
              placeholder="Boîte de réception"
              value={orphanMailboxFilter}
              onChange={setOrphanMailboxFilter}
              allowClear
              style={{ minWidth: 220 }}
              options={orphanMailboxes.map((mailbox) => ({ value: mailbox, label: mailbox }))}
            />
            <Select
              value={orphanReadFilter}
              onChange={setOrphanReadFilter}
              style={{ width: 140 }}
              options={[
                { value: 'all', label: 'Tous' },
                { value: 'unread', label: 'Non lus' },
                { value: 'read', label: 'Lus' },
              ]}
            />
            <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center' }}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {filteredOrphanEmails.length} email(s)
              </Text>
            </div>
          </div>
          <Spin spinning={orphanLoading || Object.values(attachLoadingByConversation).some(Boolean)}>
            {!orphanLoading && filteredOrphanEmails.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px 0' }}>
                <Text type="secondary">Aucun email orphelin détecté.</Text>
              </div>
            ) : (
              filteredOrphanEmails.map(email => (
                <Card 
                  key={email.id} 
                  size="small" 
                  style={{ marginBottom: 12, borderRadius: 12, border: '1px solid #e2e8f0' }}
                  styles={{ body: { padding: 14 } }}
                >
                  <Row align="middle" gutter={[16, 12]}>
                    <Col xs={24} md={16}>
                      <Space direction="vertical" size={4} style={{ width: '100%' }}>
                        <Text strong style={{ fontSize: 14, lineHeight: 1.2 }} ellipsis>
                          {email.subject || '(Sans objet)'}
                        </Text>
                        <Space size={8} wrap>
                          <Tag color={email.isRead ? 'default' : 'blue'} style={{ margin: 0 }}>
                            {email.isRead ? 'Lu' : 'Non lu'}
                          </Tag>
                          {email.conversationCount && (
                            <Tag color="purple" style={{ margin: 0 }}>
                              {email.conversationCount} mails
                            </Tag>
                          )}
                          {(email.mailboxName || email.mailboxEmail) && (
                            <Tag style={{ margin: 0 }}>
                              📥 {email.mailboxName || 'Boîte'}{email.mailboxEmail ? ` · ${email.mailboxEmail}` : ''}
                            </Tag>
                          )}
                          <Text type="secondary" style={{ fontSize: 11 }}>
                            {dayjs(email.receivedDateTime).format('DD/MM/YYYY HH:mm')}
                          </Text>
                        </Space>
                        <Text type="secondary" style={{ fontSize: 12 }} ellipsis>
                          {email.from ? `De: ${email.from} — ` : ''}
                          {email.preview}
                        </Text>
                      </Space>
                    </Col>
                    <Col xs={24} md={8} style={{ textAlign: 'right' }}>
                      {attachLoadingByConversation[email.conversationId || email.id || ''] ? (
                        <Progress
                          percent={70}
                          size="small"
                          status="active"
                          showInfo={false}
                          style={{ width: '100%', maxWidth: 130, marginLeft: 'auto' }}
                        />
                      ) : (
                        <Select
                          placeholder="Rattacher à…"
                          size="large"
                          style={{ width: '100%', maxWidth: 130 }}
                          onChange={(val) => handleAttachOrphan(email, val)}
                          showSearch
                          optionFilterProp="children"
                        >
                          {(() => {
                            const emailRetailerId = normalizeId(email.retailerId);
                            const matching = projects.filter(
                              (p) => normalizeId(p.retailerId) === emailRetailerId
                            );
                            const source = matching.length ? matching : projects;
                            return source.map((p) => (
                              <Select.Option key={p._id} value={p._id}>
                                {p.title}
                              </Select.Option>
                            ));
                          })()}
                        </Select>
                      )}
                    </Col>
                  </Row>
                </Card>
              ))
            )}
          </Spin>
        </div>
      </Modal>

      <Modal
        title="Blacklist emails"
        open={blacklistModalOpen}
        onCancel={() => setBlacklistModalOpen(false)}
        footer={null}
        width={640}
      >
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          <Card size="small" style={{ borderRadius: 12, border: '1px solid #e2e8f0' }}>
            <Space direction="vertical" size={12} style={{ width: '100%' }}>
              <Input
                value={blacklistEmail}
                onChange={(e) => setBlacklistEmail(e.target.value)}
                placeholder="email@exemple.com"
              />
              <Input
                value={blacklistReason}
                onChange={(e) => setBlacklistReason(e.target.value)}
                placeholder="Motif (optionnel)"
              />
              <Button type="primary" onClick={handleAddBlacklistEntry}>
                Ajouter à la blacklist
              </Button>
            </Space>
          </Card>
          <Spin spinning={blacklistLoading}>
            <Space direction="vertical" size={8} style={{ width: '100%' }}>
              {blacklistEntries.length === 0 ? (
                <Text type="secondary">Aucune entrée blacklist.</Text>
              ) : (
                blacklistEntries.map((entry) => (
                  <Card
                    key={entry._id}
                    size="small"
                    style={{ borderRadius: 12, border: '1px solid #e2e8f0' }}
                    styles={{ body: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' } }}
                  >
                    <Space direction="vertical" size={0}>
                      <Text strong>{entry.email}</Text>
                      {entry.reason && <Text type="secondary">{entry.reason}</Text>}
                    </Space>
                    <Button danger size="small" onClick={() => handleDeleteBlacklistEntry(entry._id)}>
                      Supprimer
                    </Button>
                  </Card>
                ))
              )}
            </Space>
          </Spin>
        </Space>
      </Modal>

      <Modal
        title={selectedProject ? "Modifier le Projet" : "Nouveau Projet"}
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        onOk={() => form.submit()}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={handleSaveProject}>
          <Form.Item name="title" label="Titre" rules={[{ required: true }]}>
            <Input placeholder="Titre du projet" />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input.TextArea rows={4} placeholder="Description détaillée..." />
          </Form.Item>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="retailerId" label="Distributeur">
                <Select allowClear placeholder="Sélectionner un distributeur">
                  {retailers.map(r => (
                    <Select.Option key={r._id || r.id} value={r._id || r.id}>{r.name}</Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="supplierId" label="Fournisseur">
                <Select allowClear placeholder="Sélectionner un fournisseur">
                  {suppliers.map(s => (
                    <Select.Option key={s._id || s.id} value={s._id || s.id}>{s.name}</Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="externalEntityName" label="Autre entité externe">
            <Input placeholder="Nom de l'entité (si pas retailer/fournisseur)" />
          </Form.Item>
          <Form.Item name="priority" label="Priorité" initialValue="medium">
            <Select>
              <Select.Option value="low">Basse</Select.Option>
              <Select.Option value="medium">Moyenne</Select.Option>
              <Select.Option value="high">Haute</Select.Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </Content>
  );
};

export default ProjectsPage;
